import React, {useState, useEffect, useRef} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import StudentWeeklyResults from "../components/StudentWeeklyResults";
import StudentDailyResults from "../components/StudentDailyResults";
import {getClassWeeklyLearningData, getStudents, getStudentWeeklyLearningData} from "../../../network/Endpoint";
import WeekSelector from "../components/WeekSelector";
import StudentSelector from "../components/StudentSelector";
import WeekDayBoxes from "../components/WeekDayBoxes";
import {cloneDeep} from "lodash";
import {useRecoilValue} from "recoil";
import {mainClassState} from "../../../recoil/classes/classesState";
import SideMenu from "../../../components/SideMenu";

const StudentWeeklyResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mainClass = useRecoilValue(mainClassState);
  
  // 데이터 로딩 상태
  const [initialLoading, setInitialLoading] = useState(true);  // 초기 로딩 상태
  const [loading, setLoading] = useState(false);  // 데이터 로딩 상태 (학생/주간 변경 시)
  const [showLoadingUI, setShowLoadingUI] = useState(false);  // 로딩 UI 표시 여부
  const loadingTimerRef = useRef(null);  // 로딩 타이머 참조
  
  // 초기 주간 설정 - 현재 날짜 기준
  const getCurrentWeek = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay; // 월요일까지의 차이 계산
    
    const mondayDate = new Date(now);
    mondayDate.setDate(now.getDate() + diffToMonday);
    
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);
    
    // 날짜 포맷 변환 (YYYY.MM.DD)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };
    
    return {
      startDate: formatDate(mondayDate),
      endDate: formatDate(sundayDate)
    };
  };

  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [studentId, setStudentId] = useState(null);
  const [students, setStudents] = useState(null);
  
  // 선택한 날짜 상태 추가
  const [selectedDate, setSelectedDate] = useState(null);
  
  // 주간 학습 데이터 상태 추가
  const [weekDays, setWeekDays] = useState([]);
  const [thisWeekRecordCounts, setThisWeekRecordCounts] = useState({});
  
  // 주간 학습 데이터 캐싱
  const [weeklyData, setWeeklyData] = useState(null);

  // 로딩 상태 변경 시 지연된 로딩 UI 표시 처리
  useEffect(() => {
    // 로딩이 시작되면 타이머 설정
    if (loading || initialLoading) {
      // 기존 타이머 정리
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      
      // 0.3초 지연 후 로딩 UI 표시
      loadingTimerRef.current = setTimeout(() => {
        if (loading || initialLoading) {
          setShowLoadingUI(true);
        }
      }, 300);
    } else {
      // 로딩이 끝나면 타이머 정리하고 로딩 UI 숨김
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoadingUI(false);
    }
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading, initialLoading]);

  // URL 파라미터 확인 및 적용
  useEffect(() => {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    const urlStudentId = searchParams.get('studentId');
    const urlSelectedDate = searchParams.get('selectedDate');
    
    if (startDateParam && endDateParam) {
      setSelectedWeek({
        startDate: startDateParam,
        endDate: endDateParam
      });
    }
    
    if (urlStudentId) {
      setStudentId(urlStudentId);
    }
    
    if (urlSelectedDate) {
      setSelectedDate(urlSelectedDate);
    }
  }, [searchParams]);

  // 날짜나 studentId가 변경될 때 URL 업데이트 (로딩 상태 변경 없이)
  useEffect(() => {
    if (selectedWeek.startDate && selectedWeek.endDate) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('startDate', selectedWeek.startDate);
      newSearchParams.set('endDate', selectedWeek.endDate);
      
      if (studentId) {
        newSearchParams.set('studentId', studentId);
      }
      
      if (selectedDate) {
        newSearchParams.set('selectedDate', selectedDate);
      } else {
        newSearchParams.delete('selectedDate');
      }
      
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedWeek, studentId, selectedDate]);

  useEffect(() => {
    if (mainClass?.classId) {
      fetchGetStudents(mainClass.classId);
    }
  }, [mainClass]);

  // 학생 목록을 가져온 후, URL에 학생 ID가 없고 학생 목록이 있으면 첫 번째 학생 선택
  useEffect(() => {
    if (students?.length > 0 && !studentId && !searchParams.get('studentId')) {
      setStudentId(students[0].studentId);
    }
  }, [students, studentId, searchParams]);

  // studentId나 selectedWeek가 변경될 때만 데이터 가져오기 (selectedDate 제외)
  useEffect(() => {
    if (studentId && selectedWeek.startDate && selectedWeek.endDate) {
      fetchStudentResults();
    }
  }, [studentId, selectedWeek]);

  const fetchGetStudents = async (classId) => {
    try {
      const body = {
        classId: mainClass.classId,
      };
      const res = await getStudents(body);

      const statusCode = res.data.status.code;
      const statusMessage = res.data.status.message;
      if (statusCode === 'Success') {
        const students = res.data.data.students;
        setStudents(cloneDeep(students));
      } else {
        alert(`ERROR: 학생 정보 가져오는 중 오류가 발생했어요.\n아크수학으로 문의해 주세요.\n${statusMessage}`);
      }
    } catch (error) {
      alert('학생 정보 가져오는 중 오류가 발생했어요.\n아크수학으로 문의해 주세요.');
    }
  };

  // API 호출하여 학생 결과 데이터 가져오기
  const fetchStudentResults = async () => {
    if (!studentId || !selectedWeek.startDate || !selectedWeek.endDate) return;
    
    // 학생이나 주간이 변경될 때만 로딩 표시
    setLoading(true);

    try {
      // API 날짜 포맷 변환 (YYYY.MM.DD -> YYYY-MM-DD)
      const convertDateFormat = (dateString) => {
        return dateString.replace(/\./g, '-');
      };
      const body =
          { studentId: studentId,
            params: {
              startDate: convertDateFormat(selectedWeek.startDate),
              endDate: convertDateFormat(selectedWeek.endDate)
            }};
      const res = await getStudentWeeklyLearningData(body);
      // API 응답 데이터 처리
      if (res.data && res.data.status.code === "Success") {
        const responseData = res.data.data;
        
        // 전체 응답 데이터 캐싱 (날짜 선택/해제 시 다시 로드하지 않도록)
        setWeeklyData(responseData);
        
        // 요일별 데이터 설정 (WeekDayBoxes 컴포넌트에서 사용)
        setThisWeekRecordCounts(responseData.thisWeekRecordCounts || {});
        
        // 주간 날짜 생성
        generateWeekDays(selectedWeek);
      } else {
        console.error('API 응답 형식이 올바르지 않습니다:', res.data);
      }
    } catch (error) {
      console.error('학생 결과를 가져오는 중 오류 발생:', error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // 월요일부터 일요일까지의 날짜 생성
  const generateWeekDays = (week) => {
    const days = [];
    
    if (week.startDate && week.endDate) {
      // startDate는 월요일, endDate는 일요일
      const startDate = new Date(week.startDate.replace(/\./g, '-'));
      
      // 이번주 날짜 생성
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        days.push({
          day: getDayNameInEnglish(i),
          date: currentDate.getDate(),
          fullDate: currentDate
        });
      }
      
      setWeekDays(days);
    }
  };

  // 숫자로부터 영어 요일명 반환
  const getDayNameInEnglish = (dayNumber) => {
    const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    return days[dayNumber];
  };

  // 학생 변경 핸들러
  const handleStudentChange = (newStudentId) => {
    setStudentId(newStudentId);
    // 학생이 변경되면 선택된 날짜 초기화
    setSelectedDate(null);
  };

  // 주간 변경 핸들러
  const handleWeekChange = (newWeek) => {
    setSelectedWeek(newWeek);
    // 주간이 변경되면 선택된 날짜 초기화
    setSelectedDate(null);
  };
  
  // 날짜 선택 핸들러
  const handleDateClick = (dayItem) => {
    if (thisWeekRecordCounts[dayItem.day] > 0) {
      const formattedDate = `${dayItem.fullDate.getFullYear()}-${String(dayItem.fullDate.getMonth() + 1).padStart(2, '0')}-${String(dayItem.fullDate.getDate()).padStart(2, '0')}`;
      
      // 이미 선택된 날짜를 다시 클릭하면 선택 해제 (로딩 없이 즉시 전환)
      if (selectedDate === formattedDate) {
        setSelectedDate(null);
      } else {
        setSelectedDate(formattedDate);
      }
    }
  };
  
  // 일일 결과에서 주간 결과로 돌아가기 핸들러
  const handleBackToWeekly = () => {
    setSelectedDate(null);
  };

  return (
      <>
      <div className="min-h-screen bg-gray-50 relative">
        <StudentSelector
          students={students}
          selectedStudentId={studentId}
          onStudentChange={handleStudentChange}
        />
        <WeekSelector
          selectedWeek={selectedWeek}
          onWeekChange={handleWeekChange}
        />

        {/* 주간 선택 박스 - 로딩 중에도 표시 (깜빡임 방지) */}
        {weekDays.length > 0 && (
          <WeekDayBoxes
            weekDays={weekDays}
            thisWeekRecordCounts={thisWeekRecordCounts}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
          />
        )}

        {/* 0.3초 이상 로딩 시에만 로딩 UI 표시 */}
        {showLoadingUI && (
          <div className="fixed inset-0 bg-white bg-opacity-70 flex justify-center items-center z-50">
            <div className="flex flex-col items-center bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-700">학습 데이터를 불러오는 중...</p>
            </div>
          </div>
        )}

        {/* 선택된 날짜가 있으면 일일 결과를, 없으면 주간 결과를 표시 (로딩 없이 즉시 전환) */}
        {selectedDate ? (
          <StudentDailyResults
            studentId={studentId}
            selectedDate={selectedDate}
            onBackToWeekly={handleBackToWeekly}
            initialLoading={true} // 초기 로딩 상태를 true로 전달하여 바로 로딩 UI를 표시하도록 함
          />
        ) : (
          !initialLoading && (
            <StudentWeeklyResults
              studentId={studentId}
              week={selectedWeek}
              loading={false} // 여기서는 항상 false로 설정 (부모에서 로딩 처리)
              selectedDate={selectedDate}
              onDateClick={handleDateClick}
              weeklyData={weeklyData} // 캐싱된 데이터 전달
            />
          )
        )}
      </div>
      <SideMenu></SideMenu>
    </>
  );
};

export default StudentWeeklyResultsPage; 